import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Activity, ListChecks } from 'lucide-react';
import { listOneDriveMigrations } from '@/api/onedriveMigration';
import { getAudit } from '@/api/audit';
import { formatDateTime } from '@/lib/dateFormat';
import type { OneDriveMigration } from '@/types/oscar';
import { isRunningInBackground } from './OffboardingStatus';

/**
 * Offboarding → Background jobs. A read-only view of the offboarding background
 * work: the scheduled timers (eligibility scan + OneDrive copy), what is running
 * in the background right now (in-progress copies), a quick status roll-up, and
 * recent activity from the audit log (scan / copy / source-delete / triggers).
 *
 * Data is reused from existing APIs — the migrations list (in-progress + roll-up)
 * and GET /api/audit?feature=Offboarding (recent activity). Timer schedules are
 * fixed in the Function App and shown here for reference (times are UTC with an
 * approximate AEST label; the crons themselves are UTC).
 */

// Fixed timer schedules (see OneDriveMigrationSyncFunction). Informational.
const SCHEDULES = [
  {
    name: 'OneDrive eligibility scan',
    cron: '0 30 17 * * * (17:30 UTC ≈ 03:30 AEST, daily)',
    detail:
      'Enqueues leavers from the Offboarding list whose termination date is 7–30 days ago. Gated by OSCAR_OFFBOARDING_ONEDRIVE_AUTOSCAN_ENABLED (default on).',
  },
  {
    name: 'OneDrive copy processor',
    cron: '0 0 8,9 * * * (fires 08:00 & 09:00 UTC; runs at whichever is 7 PM Sydney, daily — DST-aware)',
    detail:
      'Copies each due leaver’s OneDrive into the Vault, captures the shared-with manifest, and (when armed) verifies + deletes the source. Runs unbounded — unlike the on-demand button, which is capped at ~35s.',
  },
];

// Humanised label for the Offboarding audit actions.
const ACTION_LABEL: Record<string, string> = {
  OneDriveMigrationScan: 'Eligibility scan',
  OneDriveMigrationCopy: 'OneDrive copy',
  OneDriveMigrationCopyInProgress: 'OneDrive copy — in progress',
  OneDriveSourceDelete: 'Source delete',
  OneDriveMigrationManualTrigger: 'Manual trigger',
  OneDriveMigrationRetry: 'Retry',
};

function actionLabel(action: string): string {
  if (ACTION_LABEL[action]) return ACTION_LABEL[action];
  if (action.startsWith('EmailDivert')) return action.replace('EmailDivert:', 'Email divert — ');
  return action;
}

// Best-effort one-line detail from the audit NewValue JSON (falls back to Notes).
function auditDetail(newValue: string | null, notes: string | null): string {
  if (newValue) {
    try {
      const o = JSON.parse(newValue) as Record<string, unknown>;
      const parts: string[] = [];
      for (const k of ['Status', 'ItemsCopied', 'Deleted', 'Verified', 'Inserted', 'VaultFolderPath', 'Message']) {
        if (o[k] !== undefined && o[k] !== null && o[k] !== '') parts.push(`${k}: ${o[k]}`);
      }
      if (parts.length) return parts.join(' · ');
    } catch {
      /* not JSON — fall through */
    }
    return newValue.length > 160 ? `${newValue.slice(0, 160)}…` : newValue;
  }
  return notes ?? '';
}

const STATUS_ORDER = ['Pending', 'Copied', 'Failed', 'Skipped'] as const;
const statusPill: Record<string, string> = {
  Pending: 'bg-lightBlue/10 text-lightBlue',
  Copied: 'bg-green/10 text-green',
  Failed: 'bg-red/10 text-red',
  Skipped: 'bg-darkGrey/10 text-darkGrey/70',
};

export function OffboardingJobs() {
  const odQuery = useQuery({
    queryKey: ['onedrive-migrations'],
    queryFn: ({ signal }) => listOneDriveMigrations(signal),
    // Keep it fresh while watching a run.
    refetchInterval: 15_000,
  });
  const auditQuery = useQuery({
    queryKey: ['offboarding-audit'],
    queryFn: ({ signal }) => getAudit({ feature: 'Offboarding', take: 50 }, signal),
    refetchInterval: 15_000,
  });

  const migrations: OneDriveMigration[] = odQuery.data ?? [];
  const running = migrations.filter(isRunningInBackground);
  const counts = STATUS_ORDER.map((s) => ({ status: s, n: migrations.filter((m) => m.Status === s).length }));

  // Resolve a person audit row to a name using the migrations list already loaded
  // (every per-person offboarding audit row is one of these people). Falls back to
  // "person {id}" when the person isn't in the list — e.g. a since-removed row.
  const nameByPersonId = new Map<number, string>();
  for (const m of migrations) {
    const name = m.DisplayName ?? m.Username;
    if (name) nameByPersonId.set(m.PersonId, name);
  }
  const personLabel = (entityId: string): string =>
    nameByPersonId.get(Number(entityId)) ?? `person ${entityId}`;

  return (
    <section>
      <header className="mb-5">
        <Link to="/offboarding" className="mb-2 inline-flex items-center gap-1 text-xs font-semibold text-navy hover:underline">
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Offboarding
        </Link>
        <h1 className="font-heading text-2xl font-bold text-navy">Background jobs</h1>
        <p className="mt-1 text-sm text-darkGrey/80">
          The offboarding background work — scheduled timers, what&rsquo;s running now, and recent activity. Read-only.
        </p>
      </header>

      {/* Running now */}
      <div className="mb-5 rounded border border-border bg-white p-4 shadow-sm">
        <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-navy">
          <Activity className="h-4 w-4 text-orange" aria-hidden /> Running now
        </h2>
        {odQuery.isLoading && <p className="text-sm text-darkGrey/60">Loading…</p>}
        {odQuery.isError && <p className="text-sm text-red">Could not load migrations.</p>}
        {!odQuery.isLoading && !odQuery.isError && running.length === 0 && (
          <p className="text-sm text-darkGrey/60">Nothing running in the background right now.</p>
        )}
        {running.length > 0 && (
          <ul className="space-y-2">
            {running.map((m) => (
              <li key={m.OneDriveMigrationId} className="flex items-center justify-between gap-2 rounded border border-border bg-bg/40 px-3 py-2 text-sm">
                <span className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-lightBlue" aria-hidden />
                  <span className="font-mono text-navy">{m.Username ?? m.DisplayName ?? `#${m.PersonId}`}</span>
                  <span className="text-darkGrey/70">OneDrive copy</span>
                </span>
                <span className="text-xs text-darkGrey/60">
                  started {m.CopyStartedDate ? formatDateTime(m.CopyStartedDate) : '—'}
                </span>
              </li>
            ))}
          </ul>
        )}
        {/* Status roll-up */}
        <div className="mt-3 flex flex-wrap gap-2">
          {counts.map((c) => (
            <span key={c.status} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${statusPill[c.status]}`}>
              {c.status}: {c.n}
            </span>
          ))}
        </div>
      </div>

      {/* Schedules */}
      <div className="mb-5 rounded border border-border bg-white p-4 shadow-sm">
        <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-navy">
          <Clock className="h-4 w-4 text-orange" aria-hidden /> Schedules
        </h2>
        <ul className="space-y-3">
          {SCHEDULES.map((s) => (
            <li key={s.name} className="rounded border border-border bg-bg/40 px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold text-navy">{s.name}</span>
                <span className="font-mono text-xs text-darkGrey/70">{s.cron}</span>
              </div>
              <p className="mt-1 text-xs text-darkGrey/70">{s.detail}</p>
            </li>
          ))}
        </ul>
      </div>

      {/* Recent activity */}
      <div className="rounded border border-border bg-white p-4 shadow-sm">
        <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-navy">
          <ListChecks className="h-4 w-4 text-orange" aria-hidden /> Recent activity
        </h2>
        {auditQuery.isLoading && <p className="text-sm text-darkGrey/60">Loading…</p>}
        {auditQuery.isError && <p className="text-sm text-red">Could not load activity.</p>}
        {auditQuery.data && auditQuery.data.length === 0 && (
          <p className="text-sm text-darkGrey/60">No recent activity.</p>
        )}
        {auditQuery.data && auditQuery.data.length > 0 && (
          <ul className="space-y-2">
            {auditQuery.data.map((a) => (
              <li key={a.AuditId} className="rounded border border-border bg-bg/40 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-navy">
                    {actionLabel(a.Action)}
                    {a.EntityId ? <span className="ml-1 font-normal text-darkGrey/60">· {personLabel(a.EntityId)}</span> : null}
                  </span>
                  {/* Oscar audit ChangedDate is UTC → local via formatDateTime. */}
                  <span className="shrink-0 text-xs text-darkGrey/60">{formatDateTime(a.ChangedDate)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between gap-2 text-xs text-darkGrey/70">
                  <span>{auditDetail(a.NewValue, a.Notes)}</span>
                  <span className="shrink-0 text-darkGrey/50">{a.ChangedBy}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
